import { put, call, all, select, takeEvery } from "redux-saga/effects";
import _ from "lodash";
import { fetchCatalogInfo } from "lib/requests/catalog";
import {
  getScenarios,
  getScenarioEstimations,
  getScenarioTotalsByCycle,
  getScenarioEstimationsByDate,
  getScenarioEstimationsDaily,
  getScenarioEstimationsCannibalized,
} from "lib/requests/scenarios";
import {
  addCategoriesList,
  addLinesList,
  addProductsList,
  addCompetitorsList,
  addBrandsList,
  addEventsList,
} from "app/common/catalog/duck/slices";
import { addRetailersList } from "app/common/retailer/duck/slices";
import { addRetailersGroupList } from "app/common/retailers_group/duck/slices";
import { addCompaniesList } from "app/common/companies/duck/slices";
import { addAreasList } from "app/common/area/duck/slices";
import { setMetadata } from "app/common/metadata/duck/slices";
import { setSelectedDataload } from "app/common/dataload/duck/slices";
import getDefaultCycle, {
  getCycleTypeId,
  isCycleTypeById,
} from "lib/utils/cycles";
import {
  handlePathName,
  scenarioEstimationAggregatedTranslate,
  handleScenarioType,
} from "lib/utils/scenarios";
import addScenarioListAsyncAction, {
  addScenarioTotalsAsyncAction,
  changeSelectedScenarioAsyncAction,
  deleteScenarioTotalsAsyncAction,
} from "./actions";
import {
  addScenarioList,
  addScenarioTotals,
  deleteScenarioTotals,
  setSelectedScenario,
  setReferenceScenario,
  setScenarioEstimations,
  setLoadingScenario,
  setCacheScenario,
  setCacheReferenceScenario,
  setCannibalizationRequested,
} from "./slices";

const scenarioEstimationByDateTranslateFields = {
  avg_cost: "cost",
  avg_price_in: "price_in",
  avg_price: "price",
  sum_investment: "investment",
  sum_investment_in: "investment_in",
  sum_margin: "margin",
  sum_units: "units",
  sum_units_in: "units_in",
  sum_sales: "sales",
  sum_sales_in: "sales_in",
  avg_pct_price_cross: "pct_price_cross",
  avg_weight_by_units_price: "price",
  avg_weight_by_units_in_price_in: "price_in",
  avg_weight_by_units_price_out_wo_taxes: "price_out_wo_taxes",
  avg_weight_by_units_in_price_in_wo_taxes: "price_in_wo_taxes",
};

function* addScenarioListAsync(action) {
  const scenarios = yield call(getScenarios, {
    filters: action.payload.filters,
  });

  yield put(addScenarioList(_.keyBy(scenarios, "id")));
}

export default function* watchAddScenarioListAsync() {
  yield takeEvery(addScenarioListAsyncAction, addScenarioListAsync);
}

function* addScenarioTotalsAsync(action) {
  const queryParams = {};

  if (action.payload.filters) queryParams.filters = action.payload.filters;
  if (action.payload.by) queryParams.by = action.payload.by;
  if (action.payload.expand) queryParams.expand = action.payload.expand;

  const scenarioTotals = yield call(getScenarioTotalsByCycle, queryParams);

  yield put(
    addScenarioTotals(
      _.keyBy(scenarioTotals, (scenarioTotal) => scenarioTotal.scenario.id)
    )
  );

  // We only want to enter here the first time right after login, when no scenario or reference scenario is set
  const scenariosSlice = yield select((state) => state.scenarios);

  if (_.isEmpty(scenariosSlice.selectedScenario)) {
    const location = yield call(() => window.location.href);
    const cycleSlice = yield select((state) => state.cycle);
    const cycleType = handlePathName(location);
    const cycleListByCycleType = [];
    const cycleTypeList = (label) => {
      const cycleTypeData = getCycleTypeId(label, cycleSlice);
      return _.filter(cycleSlice.list, (cycle) => {
        return cycle.cycle_type_id === cycleTypeData[0].id;
      });
    };
    if (cycleType === "Trade Promotions") {
      _.forEach(["Trade Promotions", "Daily Trade Promotions"], (value) => {
        cycleListByCycleType.push(...cycleTypeList(value));
      });
    } else {
      _.forEach(["Pricing", "Pricing 2.0"], (value) => {
        cycleListByCycleType.push(...cycleTypeList(value));
      });
    }

    if (_.isEmpty(cycleListByCycleType)) return "";

    const closestFutureCycle = getDefaultCycle(cycleListByCycleType);

    const initialSelectedScenarioTotals = _.find(
      scenarioTotals,
      (scenarioTotal) =>
        scenarioTotal.scenario.scenario_type_id === 1 &&
        scenarioTotal.scenario.cycle_id === closestFutureCycle
    );
    const currentCycleTypeLabel = handleScenarioType(
      initialSelectedScenarioTotals.scenario.cycle.cycle_type_id
    );
    yield all([
      put(
        changeSelectedScenarioAsyncAction(
          initialSelectedScenarioTotals.scenario.id
        )
      ),
      put(setReferenceScenario(initialSelectedScenarioTotals)),
      put(
        setCacheScenario({
          [currentCycleTypeLabel]: initialSelectedScenarioTotals,
        })
      ),
      put(
        setCacheReferenceScenario({
          [currentCycleTypeLabel]: initialSelectedScenarioTotals,
        })
      ),
    ]);
  }

  return scenariosSlice.totalsByScenarioId;
}

export function* watchAddScenarioTotalsAsync() {
  yield takeEvery(addScenarioTotalsAsyncAction, addScenarioTotalsAsync);
}

function* changeSelectedScenarioAsync(action) {
  const scenariosSlice = yield select((state) => state.scenarios);
  const cycleSlice = yield select((state) => state.cycle);
  const catalogSlice = yield select((state) => state.catalog);
  const retailersSlice = yield select((state) => state.retailers);
  const areasSlice = yield select((state) => state.areas);
  const metadataSlice = yield select((state) => state.metadata);

  yield put(setLoadingScenario(true));

  let scenarioEstimations;
  let scenarioTotals = scenariosSlice.totalsByScenarioId[action.payload];

  // If scenario id is not in the scenario totals list, update the list
  if (!scenarioTotals) {
    const totalsByScenarioId = yield* addScenarioTotalsAsync({
      payload: {
        filters: {
          scenario_type_id: [1, 3],
        },
        expand: ["scenario", "scenario.cycle"],
      },
    });
    scenarioTotals = totalsByScenarioId[action.payload];
  }

  // We need to compute cannibalized estimations so the backend can give us the
  // the cannibalized totals right after we compute this block
  if (scenarioTotals.scenario.is_blocked) {
    scenarioEstimations = yield call(getScenarioEstimationsCannibalized, {
      scenario_id: action.payload,
    });
    scenarioEstimations = scenarioEstimationAggregatedTranslate(
      scenarioEstimationByDateTranslateFields,
      scenarioEstimations
    );
  }

  // Get most recent scenario totals, if the block above was executed, it will bring
  // cannibalized totals, if it was not executed, it will bring normal totals
  const [newTotals] = yield call(getScenarioTotalsByCycle, {
    filters: {
      scenario_type_id: [1, 3],
      scenario: { id: action.payload },
    },
    expand: ["scenario", "scenario.cycle"],
  });

  // We need to update the store totalsByScenarioId with the new totals because it
  // will keep those sent by the server at the first render, and we want them updated
  // with every cannibalization switch
  const obj = {
    ...scenariosSlice.totalsByScenarioId,
    [action.payload]: {
      ...newTotals,
    },
  };
  yield put(addScenarioTotals({ ...obj }));

  if (!scenarioTotals.scenario.is_blocked) {
    switch (
      isCycleTypeById(scenarioTotals.scenario.cycle.cycle_type_id, cycleSlice)
    ) {
      case "Pricing 2.0":
        scenarioEstimations = yield call(getScenarioEstimationsByDate, {
          filters: {
            scenario: { id: action.payload },
            group_by: ["product_id", "area_id", "retailer_id"],
            sum: [
              "units",
              "margin",
              "units_in",
              "sales",
              "sales_in",
              "investment",
              "investment_in",
              "n_stores",
            ],
            avg: ["pct_price_cross", "price", "price_in"],
            avg_weight_by_units: ["price_out_wo_taxes"],
            avg_weight_by_units_in: ["price_in_wo_taxes"],
          },
        });
        scenarioEstimations = scenarioEstimationAggregatedTranslate(
          scenarioEstimationByDateTranslateFields,
          scenarioEstimations
        );
        break;
      case "Daily Trade Promotions":
        scenarioEstimations = yield call(getScenarioEstimationsDaily, {
          filters: {
            scenario: { id: action.payload },
            group_by: ["product_id", "area_id", "retailer_id"],
            sum: [
              "units",
              "margin",
              "units_in",
              "sales",
              "sales_in",
              "investment",
              "investment_in",
              "n_stores",
            ],
            avg_weight_by_units: ["price", "price_out_wo_taxes"],
            avg_weight_by_units_in: ["price_in", "price_in_wo_taxes"],
          },
        });
        scenarioEstimations = scenarioEstimationAggregatedTranslate(
          scenarioEstimationByDateTranslateFields,
          scenarioEstimations
        );
        break;
      default:
        scenarioEstimations = yield call(getScenarioEstimations, {
          filters: {
            scenario: { id: action.payload },
          },
        });
        break;
    }
  }

  let catalogForDataload;

  if (
    scenarioTotals.delivery_id !== scenariosSlice.selectedScenario.delivery_id
  ) {
    catalogForDataload = yield fetchCatalogInfo();
    const retailersGroupList = _.keyBy(
      _.map(
        _.uniqBy(Object.values(catalogForDataload.retailers), (item) => {
          return item.retailer_groups.id;
        }),
        "retailer_groups"
      ),
      "id"
    );
    yield all([
      put(addCategoriesList(catalogForDataload.categories)),
      put(addLinesList(catalogForDataload.lines)),
      put(addBrandsList(catalogForDataload.brands)),
      put(addProductsList(catalogForDataload.products)),
      put(addEventsList(catalogForDataload.events)),
      put(addCompetitorsList(catalogForDataload.competitors)),
      put(addCompaniesList(catalogForDataload.companies)),
      put(addAreasList(catalogForDataload.areas)),
      put(addRetailersList(catalogForDataload.retailers)),
      put(addRetailersGroupList(retailersGroupList)),
      put(setMetadata(catalogForDataload.metadata)),
      put(setSelectedDataload({ id: scenarioTotals.delivery_id })),
    ]);
  } else {
    catalogForDataload = {
      ...catalogSlice,
      retailers: retailersSlice,
      areas: areasSlice,
      metadata: metadataSlice,
    };
  }

  const scenarioEstimationsWithExpandedProperties = _.map(
    scenarioEstimations,
    (scenarioEstimation) => {
      return {
        ...scenarioEstimation,
        price: _.round(
          scenarioEstimation.price,
          catalogForDataload.metadata["custom-projection"].price[0].decimals
        ),
        price_in: _.round(
          scenarioEstimation.price_in,
          catalogForDataload.metadata["custom-projection"].price_in[0].decimals
        ),
        product: catalogForDataload.products[scenarioEstimation.product_id],
        retailer: catalogForDataload.retailers[scenarioEstimation.retailer_id],
        area: catalogForDataload.areas[scenarioEstimation.area_id],
      };
    }
  );

  yield put(
    setScenarioEstimations({
      id: action.payload,
      estimations: _.keyBy(
        scenarioEstimationsWithExpandedProperties,
        (estimation) =>
          `${estimation.product_id}-${estimation.area_id}-${estimation.retailer_id}`
      ),
    })
  );

  let referenceScenarioValues = {};
  let scenarioSelectedValues = {};
  const currentCycleTypeLabel = handleScenarioType(
    scenarioTotals.scenario.cycle.cycle_type_id
  );

  // The selected scenario is the reference for the cycle
  if (scenarioTotals.scenario.scenario_type_id === 1) {
    referenceScenarioValues = {
      ...scenarioTotals,
      estimations: _.keyBy(
        scenarioEstimationsWithExpandedProperties,
        (estimation) =>
          `${estimation.product_id}-${estimation.area_id}-${estimation.retailer_id}`
      ),
      estimationsByDate: {},
    };
    scenarioSelectedValues = {
      ...scenarioTotals,
      estimations: _.keyBy(
        scenarioEstimationsWithExpandedProperties,
        (estimation) =>
          `${estimation.product_id}-${estimation.area_id}-${estimation.retailer_id}`
      ),
      estimationsByDate: {},
    };
    // We need to remove oposite scenario when is Trade Promotions we will remove Daily Trade Promotions cache and vice versa
    let currentCycleCache = {};
    switch (currentCycleTypeLabel) {
      case "Trade Promotions":
        currentCycleCache = {
          [currentCycleTypeLabel]: scenarioSelectedValues,
          "Daily Trade Promotions": {},
        };
        break;
      case "Daily Trade Promotions":
        currentCycleCache = {
          [currentCycleTypeLabel]: scenarioSelectedValues,
          "Trade Promotions": {},
        };
        break;
      default:
        currentCycleCache = { [currentCycleTypeLabel]: scenarioSelectedValues };
    }
    yield all([
      put(setReferenceScenario(referenceScenarioValues)),
      put(setSelectedScenario(scenarioSelectedValues)),
      put(setCacheScenario(currentCycleCache)),
      put(
        setCacheReferenceScenario({
          [currentCycleTypeLabel]: referenceScenarioValues,
        })
      ),
    ]);
  }

  // The selected scenario is from a cycle different than the previous selected one, thus we need to also request the reference for that cycle
  else if (
    scenarioTotals.scenario.cycle_id !==
    scenariosSlice.referenceScenario.scenario.cycle_id
  ) {
    const newReferenceScenario = _.find(
      scenariosSlice.totalsByScenarioId,
      (scenarioTotal) => {
        return (
          scenarioTotal.scenario.cycle_id ===
            scenarioTotals.scenario.cycle_id &&
          scenarioTotal.scenario.scenario_type_id === 1
        );
      }
    );
    let referenceEstimations;

    if (!newReferenceScenario.scenario.is_blocked) {
      switch (
        isCycleTypeById(scenarioTotals.scenario.cycle.cycle_type_id, cycleSlice)
      ) {
        case "Pricing 2.0":
          referenceEstimations = yield call(getScenarioEstimationsByDate, {
            filters: {
              scenario: { id: newReferenceScenario.scenario_id },
              group_by: ["product_id", "area_id", "retailer_id"],
              sum: [
                "units",
                "margin",
                "units_in",
                "sales",
                "sales_in",
                "investment",
                "investment_in",
                "n_stores",
              ],
              avg: ["pct_price_cross", "price", "price_in"],
              avg_weight_by_units: ["price_out_wo_taxes"],
              avg_weight_by_units_in: ["price_in_wo_taxes"],
            },
          });
          referenceEstimations = scenarioEstimationAggregatedTranslate(
            scenarioEstimationByDateTranslateFields,
            referenceEstimations
          );
          break;
        case "Daily Trade Promotions":
          referenceEstimations = yield call(getScenarioEstimationsDaily, {
            filters: {
              scenario: { id: newReferenceScenario.scenario_id },
              group_by: ["product_id", "area_id", "retailer_id"],
              sum: [
                "units",
                "margin",
                "units_in",
                "sales",
                "sales_in",
                "investment",
                "investment_in",
                "n_stores",
              ],
              avg_weight_by_units: ["price", "price_out_wo_taxes"],
              avg_weight_by_units_in: ["price_in", "price_in_wo_taxes"],
            },
          });
          referenceEstimations = scenarioEstimationAggregatedTranslate(
            scenarioEstimationByDateTranslateFields,
            referenceEstimations
          );
          break;
        default:
          referenceEstimations = yield call(getScenarioEstimations, {
            filters: {
              scenario: { id: newReferenceScenario.scenario_id },
            },
          });
      }
    } else {
      const scenarioReferenceEstimations = yield call(
        getScenarioEstimationsCannibalized,
        {
          scenario_id: newReferenceScenario.scenario_id,
        }
      );
      referenceEstimations = scenarioEstimationAggregatedTranslate(
        scenarioEstimationByDateTranslateFields,
        scenarioReferenceEstimations
      );
    }

    const referenceEstimationsWithExpandedProperties = _.map(
      referenceEstimations,
      (referenceEstimation) => {
        return {
          ...referenceEstimation,
          price: _.round(
            referenceEstimation.price,
            catalogForDataload.metadata["custom-projection"].price[0].decimals
          ),
          price_in: _.round(
            referenceEstimation.price_in,
            catalogForDataload.metadata["custom-projection"].price_in[0]
              .decimals
          ),
          product: catalogForDataload.products[referenceEstimation.product_id],
          retailer:
            catalogForDataload.retailers[referenceEstimation.retailer_id],
          area: catalogForDataload.areas[referenceEstimation.area_id],
        };
      }
    );

    referenceScenarioValues = {
      ...newReferenceScenario,
      estimations: _.keyBy(
        referenceEstimationsWithExpandedProperties,
        (estimation) =>
          `${estimation.product_id}-${estimation.area_id}-${estimation.retailer_id}`
      ),
      estimationsByDate: {},
    };
    scenarioSelectedValues = {
      ...scenarioTotals,
      estimations: _.keyBy(
        scenarioEstimationsWithExpandedProperties,
        (estimation) =>
          `${estimation.product_id}-${estimation.area_id}-${estimation.retailer_id}`
      ),
      estimationsByDate: {},
    };
    // We need to remove oposite scenario when is Trade Promotions we will remove Daily Trade Promotions cache and vice versa
    let currentCycleCache = {};
    switch (currentCycleTypeLabel) {
      case "Trade Promotions":
        currentCycleCache = {
          [currentCycleTypeLabel]: scenarioSelectedValues,
          "Daily Trade Promotions": {},
        };
        break;
      case "Daily Trade Promotions":
        currentCycleCache = {
          [currentCycleTypeLabel]: scenarioSelectedValues,
          "Trade Promotions": {},
        };
        break;
      default:
        currentCycleCache = { [currentCycleTypeLabel]: scenarioSelectedValues };
    }

    yield all([
      put(setReferenceScenario(referenceScenarioValues)),
      put(setSelectedScenario(scenarioSelectedValues)),
      put(setCacheScenario(currentCycleCache)),
      put(
        setCacheReferenceScenario({
          [currentCycleTypeLabel]: referenceScenarioValues,
        })
      ),
    ]);
  }
  // If the reference scenario does not change we simply update the selected scenario
  else {
    scenarioSelectedValues = {
      ...scenarioTotals,
      estimations: _.keyBy(
        scenarioEstimationsWithExpandedProperties,
        (estimation) =>
          `${estimation.product_id}-${estimation.area_id}-${estimation.retailer_id}`
      ),
      estimationsByDate: {},
    };
    yield all([
      put(setSelectedScenario(scenarioSelectedValues)),
      put(
        setCacheScenario({ [currentCycleTypeLabel]: scenarioSelectedValues })
      ),
    ]);
  }

  yield put(setLoadingScenario(false));
  // the following lines are necessary for the cannibalization snackbar
  if (scenariosSlice.cannibalizationRequested === "loading")
    yield put(setCannibalizationRequested("finished"));
}

export function* watchChangeSelectedScenarioAsync() {
  yield takeEvery(
    changeSelectedScenarioAsyncAction,
    changeSelectedScenarioAsync
  );
}

export function* watchAddScenarioList() {
  yield takeEvery(addScenarioList, function* addScenariosTotalsForList(action) {
    yield put(
      addScenarioTotalsAsyncAction({
        filters: {
          scenario_id: [_.map(action.payload, (scenario) => scenario.id)],
        },
        expand: ["scenario", "scenario.cycle"],
      })
    );
  });
}

export function* deleteScenarioTotalsAsync(action) {
  const scenariosSlice = yield select((state) => state.scenarios);

  const selectedScenario = scenariosSlice.selectedScenario.scenario;

  // The currently selected scenario was removed, select Reference scenario
  if (selectedScenario.id === action.payload.scenario.id) {
    yield put(
      changeSelectedScenarioAsyncAction(
        scenariosSlice.referenceScenario.scenario.id
      )
    );
  }

  yield put(deleteScenarioTotals({ scenarioId: action.payload.scenario.id }));
}

export function* watchDeleteScenarioTotalsAsync() {
  yield takeEvery(deleteScenarioTotalsAsyncAction, deleteScenarioTotalsAsync);
}
